import React, { useState, useCallback, useMemo } from 'react'
import cn from 'classnames'
import { map, get } from 'lodash'

import useGetIsMobileScreen from '../../hooks/useGetIsMobileScreen'
import quantityValidator from '../../utils/quantityValidator'

import styles from './VNViewItemDetail.module.scss'
import TextField from '@material-ui/core/TextField'
import { makeStyles } from '@material-ui/core/styles'
import ModifierGroup from '../../components/ModifierGroup'
import { Button } from '@material-ui/core'
import SidebarModal from '../../components/SidebarModal'
import { useDispatch, useSelector } from 'react-redux'
import { getItemForCurrentMenuById } from '../../selectors/items'
import VNPriceIndicator from './VNPriceIndicator'
import { getLatestPriceAdjustment } from '../../VNDialogs/Selectors'
import { setPriceAdjustDialog, setPriceAdjustExitConfirmation } from '../../VNDialogs/ActionCreators'
import usePriceAdjustment from '../hooks/usePriceAdjustment'
import { getManagerId } from '../../selectors/attendant'

const textFieldStyles = makeStyles({
  root: {
    // focused color for input with variant='outlined'
    '& .MuiOutlinedInput-root': {
      '&.MuiInputBase-formControl fieldset': {
        border: 0,
      },
      '&.Mui-focused fieldset': {
        border: 0,
      },
    },
  },
})

/**
 * VNViewItemDetail - Used to display item details and adjust price.
 */
const VNViewItemDetail = ({
  quantity = 1,
  notes = '',
  selectedModifiers = [[]],
  className,
  itemUuid,
  onClose,
  priceInCents,
  originalPriceInCents,
  name = '',
  orderUuid,
  lineItemUuids,
  wasCreatedInStadium,
  ...props
}) => {
  const dispatch = useDispatch()
  const menuItem = useSelector((state) => getItemForCurrentMenuById(state, itemUuid))
  const managerId = useSelector(getManagerId)
  const priceAdjustmentInCents = useSelector(getLatestPriceAdjustment)

  const { adjustPriceOffline, adjustPriceOnline } = usePriceAdjustment(orderUuid)

  const modifierGroups = useMemo(() => menuItem.modifierGroups || [], [menuItem.modifierGroups])
  const classes = textFieldStyles()
  const isMobile = useGetIsMobileScreen()

  const modifierGroupsSelections = useMemo(() => {
    const selectedMods = []
    modifierGroups.forEach((modifierGroup) => {
      const group = []
      modifierGroup.items.forEach((item) => {
        const found = selectedModifiers.find((selectedModifier) => {
          return selectedModifier.id === item.id
        })
        if (found) {
          group.push(`${item.id}|${modifierGroup.uuid}`)
        }
      })
      selectedMods.push(group)
    })
    return selectedMods
  }, [modifierGroups, selectedModifiers])

  const hasPriceAdjustment = useMemo(() => {
    if (priceAdjustmentInCents === null) {
      return false
    }

    //need to look for new price vs item price
    return priceAdjustmentInCents !== priceInCents
  }, [priceAdjustmentInCents, priceInCents])

  const [validators] = useState(map(modifierGroups, ({ min, max }) => quantityValidator(min, max)))
  const clientHeight = document?.body?.clientHeight

  /**
   *
   * @param {number} priceInCents - The price of the item in cents
   * @param {number} originalPriceInCents - The original price of the item in cents
   * @param {number} priceAdjustmentInCents - The current price adjustment of the item in cents
   */
  const VNSelectPriceIndicatorProps = ({ priceInCents, originalPriceInCents, priceAdjustmentInCents }) => {
    //we have an item with a price adjustment in progress
    if (priceAdjustmentInCents !== null) {
      //we need to send the original price in cents or the price in cents
      const defaultPriceIncents = originalPriceInCents ?? priceInCents
      return (
        <VNPriceIndicator
          defaultPriceInCents={defaultPriceIncents}
          priceAdjustmentInCents={priceAdjustmentInCents}
          fromViewItemDetail={true}
        />
      )
    }

    //An item that already has a price adjustment applied with no adjustment in progress
    //if originalPriceInCents has a valid value then we can tell the item had a price adjustment
    if (originalPriceInCents !== null && originalPriceInCents !== undefined) {
      const defaultPriceIncents = originalPriceInCents
      const priceAdjustmentInCents = priceInCents
      return (
        <VNPriceIndicator
          defaultPriceInCents={defaultPriceIncents}
          priceAdjustmentInCents={priceAdjustmentInCents}
          fromViewItemDetail={true}
        />
      )
    }

    //An item without any type of price adjustment
    return <VNPriceIndicator defaultPriceInCents={priceInCents} priceAdjustmentInCents={null} fromViewItemDetail={true} />
  }

  const NotesComponent = () => {
    if (!notes) return null
    return (
      <div className={styles.notesContainer}>
        <TextField
          disabled={true}
          id="special-instructions"
          label="Special Instructions"
          value={notes}
          className={cn(styles.notes, classes.root)}
          InputLabelProps={{ className: styles.textfield__label }}
          inputProps={{ 'data-allow-keyboard-activity': true }}
          InputProps={{ className: styles.textfield__input }}
          variant="outlined"
          multiline
          rows={5}
        />
      </div>
    )
  }

  const handleSubmit = () => {
    if (hasPriceAdjustment) {
      if (!wasCreatedInStadium) {
        adjustPriceOffline({
          lineItemUuids,
          priceInCents: priceAdjustmentInCents,
          originalPriceInCents: originalPriceInCents || priceInCents,
          managerId,
        })
      } else {
        adjustPriceOnline({ menuItemUuid: itemUuid, lineItemUuids, priceInCents: priceAdjustmentInCents, managerId })
      }
    }
    dispatch(setPriceAdjustDialog(false))
    onClose()
  }

  const handleClose = useCallback(() => {
    if (hasPriceAdjustment) {
      dispatch(setPriceAdjustExitConfirmation(true, true, onClose))
      return
    }
    onClose()
  }, [onClose, dispatch, hasPriceAdjustment])

  return (
    <SidebarModal show title="Item Detail" onClose={handleClose} displayOverlay={false} {...props}>
      <div className={styles.cartItemDetailsModal}>
        <div className={styles.scrollable}>
          <div className={styles.header}>
            <div className={styles.itemName}>{name}</div>
            <VNSelectPriceIndicatorProps
              priceInCents={priceInCents}
              originalPriceInCents={originalPriceInCents}
              priceAdjustmentInCents={priceAdjustmentInCents}
            />
          </div>
          {modifierGroups.map((modifierGroup, index) => {
            if (!modifierGroup.name) {
              return null
            }
            return (
              <ModifierGroup
                disabled={true}
                key={index}
                className={styles.modifierGroup}
                title={modifierGroup.name}
                label={get(validators, `[${index}].label`)}
                items={modifierGroup.items}
                selectedItemIds={modifierGroupsSelections?.[index]}
                uuid={modifierGroup.uuid}
              />
            )
          })}
          <NotesComponent />
        </div>

        <div className={styles.footer} style={isMobile && clientHeight > 800 ? { top: '48vh' } : null}>
          <div className={styles.quantityContainer}>
            <div className={styles.quantity}>{quantity}</div>
          </div>
          <div className={styles.submitContainer}>
            <Button className={styles.submit} disabled={false} onClickCapture={handleSubmit}>
              <span>Update</span>
            </Button>
          </div>
        </div>
      </div>
    </SidebarModal>
  )
}

export default VNViewItemDetail
